import axios from 'axios'
import { computed, createApp, defineAsyncComponent, reactive, ref } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { MotionPlugin } from '@vueuse/motion'
import { port, apiDomain, useDevAPI } from '../../config/config/settings'
import { useStore, useCheckout } from './composables/store'
import VueClipboard from 'vue3-clipboard'
import Vue3TouchEvents from 'vue3-touch-events'
import App from './App.vue'
import './index.css'

/*
    If these are dynamicly imported I end up with a blank
    screen while the route is waiting for the component
    to download... not sure how to fix.
*/

String.prototype.toTitleCase = function () {
	return this.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
	})
}

import SignUp from './components/pages/sign-up.vue'
import SignUpError from './components/pages/sign-up-error.vue'
import Welcome from './components/pages/welcome.vue'
import Login from './components/layout/login.vue'

import Home from './components/pages/home.vue'
import AboutUs from './components/pages/about-us.vue'
import SignIn from './components/pages/sign-in.vue'
import ItemID from './components/pages/item.vue'
import Product from './components/pages/product.vue'
import Checkout from './components/checkout/checkout.vue'
import Cart from './components/pages/ihh-items/cart.vue'
// CartCopy import removed as it was unused
import OrderConfirmation from './components/pages/order-confirmation.vue'
import TermsConditions from './components/pages/terms-conditions.vue'
import HelpCenter from './components/pages/help-center.vue'
import Returns from './components/pages/returns.vue'
import Brands from './components/pages/brands.vue'
import Midwife from './components/pages/midwife-birth-kits.vue'
import NewBirthKit from './components/pages/new-birth-kit-setup.vue'
import Shipping from './components/pages/shipping-info.vue'
import CustomBirthKit from './components/pages/custom-birth-kit-service.vue'
import Lost from './components/pages/lost.vue'
import DiscountTerms from './components/pages/discount-code-terms.vue'
import SiteMap from './components/pages/sitemap.vue'
import SitemapBrands from './components/pages/sitemap-brands.vue'
import SitemapCategories from './components/pages/sitemap-categories.vue'
import PrivacyPolicy from './components/pages/privacy-policy.vue'
import SMS from './components/pages/sms.vue'
import BirthPoolSetup from './components/pages/birth-pool-setup.vue'
import NewmanDopplerTradeInOffer from './components/pages/newman-doppler-trade-in-offer.vue'
import GiftCertificates from './components/pages/gift-certificates.vue'
import BirthKit from './components/pages/birth-kit.vue'
import BirthKitBuilder from './components/pages/birth-kit-builder.vue'
import BirthKitOrderForm from './components/pages/birth-kit-order-form.vue'
import CustomBirthKitUpdates from './components/pages/custom-birth-kit-updates.vue'
import CustomBirthKitBlogPost from './components/pages/custom-birth-kit-blog-post.vue'
import NotFound from './components/pages/404.vue'
import Search from './components/pages/search.vue'
import FAQS from './components/pages/ihh-items/faqs.vue'
import TermsAndConditions from './components/pages/ihh-items/terms-and-conditions.vue'

import Category from './components/pages/category.vue'
import Brand from './components/pages/brand.vue'

const isDev = process.env.NODE_ENV != 'production'

const Account = () => import('./components/pages/account.vue')
const accountAddresses = () => import('./components/account/addresses.vue')
const accountAddressForm = () => import('./components/account/address-form.vue')
const accountOrders = () => import('./components/account/orders.vue')
const accountOrderDetails = () => import('./components/account/order-details.vue')
const accountRecentlyViewed = () => import('./components/account/recently-viewed.vue')
const accountSettings = () => import('./components/account/settings.vue')
const accountWishlists = () => import('./components/account/wishlists.vue')
const accountBirthKits = () => import('./components/account/birth-kits.vue')
const accountClaims = () => import('./components/account/claims.vue')
const accountApprovals = () => import('./components/account/approvals.vue')
const accountWishlistForm = () => import('./components/account/wishlist-form.vue')
const sharedWishlist = () => import('./components/pages/shared-wishlists.vue')
const invoiceTemplate = () => import('./components/account/order-invoice.vue')

const routes = [
	// Special handling for PHP files first
	{
		path: '/:pathMatch(.*)\.php',
		redirect: (to) => {
			// Map specific PHP files to their Vue routes
			const phpRouteMap = {
				'giftcertificates.php': '/gift-certificates',
				'orderstatus.php': '/account/orders',
			}

			// Get the PHP filename from the path
			const phpFile = to.path.split('/').pop()

			// If we have a mapping for this PHP file, use it, otherwise go to 404
			const targetPath = phpRouteMap[phpFile] || '/404'

			return { path: targetPath, query: to.query }
		},
	},
	{ path: '/', component: Home, meta: { title: 'In His Hands Birth Supply' } },
	{ path: '/about-us', component: AboutUs, meta: { title: 'About Us | In His Hands Birth Supply' } },
	{ path: '/sign-up-error', component: SignUpError },
	{ path: '/login', component: Login, meta: { breadcrumb: 'Login', title: 'Login | In His Hands Birth Supply' } },
	{
		path: '/welcome',
		component: Welcome,
		props: (route) => ({ email: route.query.email }),
		meta: { title: 'Welcome | In His Hands Birth Supply' },
	},
	{
		path: '/account',
		component: Account,
		children: [
			{ path: '', component: accountOrders, meta: { breadcrumb: 'Your Orders' } },
			{ path: 'addresses', component: accountAddresses, meta: { breadcrumb: 'Address Book' } },
			{ path: 'addresses/create', component: accountAddressForm, meta: { breadcrumb: 'Create', parent: 'Address Book' } },
			{ path: 'addresses/:id', component: accountAddressForm, meta: { breadcrumb: 'Address Form', parent: 'Address Book' } },
			{ path: 'orders', component: accountOrders, meta: { breadcrumb: 'Your Orders' } },
			{ path: 'orders/:id', component: accountOrderDetails, meta: { breadcrumb: 'Order Details' } },
			{ path: 'recently-viewed', component: accountRecentlyViewed, meta: { breadcrumb: 'Recently Viewed Items' } },
			{ path: 'settings', component: accountSettings, meta: { breadcrumb: 'Account Details' } },
			{ path: 'birth-kits', component: accountBirthKits, meta: { breadcrumb: 'Your Birth Kits' } },
			{ path: 'claims', component: accountClaims, meta: { breadcrumb: 'Kit Claims', adminOnly: true } },
			{ path: 'approvals', component: accountApprovals, meta: { breadcrumb: 'Pending Approvals', adminOnly: true } },
			{ path: 'lists', component: accountWishlists, meta: { breadcrumb: 'Your Wishlists' } },
			{ path: 'lists/create', component: accountWishlistForm, meta: { breadcrumb: 'Create' } },
			{ path: 'lists/:id', component: accountWishlistForm, meta: { breadcrumb: 'Edit Wishlist' } },
		],
		meta: { title: 'Your Account | In His Hands Birth Supply', breadcrumb: 'Your Account', protected: true, fallback: '/login' },
	},
	{
		path: '/shared-list',
		component: sharedWishlist,
		meta: { title: 'Shared Wishlist | In His Hands Birth Supply', breadcrumb: 'Shared Wishlist' },
	},
	{
		path: '/invoice/:id',
		component: invoiceTemplate,
		name: 'invoiceTemplate',
		meta: {
			title: 'Invoice | In His Hands Birth Supply',
			protected: true,
			fallback: '/login',
		},
	},

	// { path: '/products', component: Products },
	{ path: '/products/:id', component: Product, meta: { usePathKey: true } },

	{ path: '/item/:id', component: ItemID, meta: { usePathKey: true } },
	{ path: '/cart', component: Cart, meta: { title: 'Your Cart | In His Hands Birth Supply' } },
	{ path: '/checkout', component: Checkout, meta: { title: 'Checkout | In His Hands Birth Supply', usePathKey: true } },
	{ path: '/checkout/order-confirmation/:bcId', component: OrderConfirmation, meta: { title: 'Order Confirmation | In His Hands Birth Supply' } },
	{ path: '/terms-conditions', component: TermsConditions, meta: { title: 'Terms & Conditions | In His Hands Birth Supply' } },
	{ path: '/help-center', component: HelpCenter, meta: { title: 'Help Center | In His Hands Birth Supply' } },

	{ path: '/categories', component: Category, meta: { usePathKey: true } },
	{ path: '/categories/:id', component: Category, meta: { usePathKey: true } },
	{ path: '/brands/:id', component: Brand, meta: { usePathKey: true } },

	// { path: '/birth-kits', component: BirthKits },
	{ path: '/birthKits/:id', component: BirthKit },
	{ path: '/birthKits/:id/edit', component: BirthKitBuilder },
	{ path: '/birth-kit-order-form', component: BirthKitOrderForm },
	{ path: '/custom-birth-kit-updates', component: CustomBirthKitUpdates, meta: { title: 'Custom Birth Kit Updates' } },
	{ path: '/blogs', component: CustomBirthKitUpdates, meta: { title: 'Custom Birth Kit Updates' } },
	{ path: '/blogs/:slug', component: CustomBirthKitBlogPost, meta: { title: 'Custom Birth Kit Updates' } },

	{ path: '/returns', component: Returns, meta: { title: 'Returns | In His Hands Birth Supply' } },
	{ path: '/brands', component: Brands, meta: { title: 'Brands | In His Hands Birth Supply' } },
	{ path: '/midwife-birth-kits', component: Midwife, meta: { title: 'Midwife Birth Kits' } },
	{ path: '/new-birth-kit-setup', component: NewBirthKit, meta: { title: 'New Birth Kit Setup' } },
	{ path: '/setup', redirect: '/new-birth-kit-setup', meta: { title: 'New Birth Kit Setup' } },
	{ path: '/shipping-info', component: Shipping, meta: { title: 'Shipping Info | In His Hands Birth Supply' } },
	{ path: '/custom-birth-kit-service', component: CustomBirthKit, meta: { title: 'Custom Birth Kit Service' } },
	{ path: '/lost', component: Lost, meta: { title: 'Lost | In His Hands Birth Supply' } },
	{ path: '/faqs', component: FAQS, meta: { title: 'FAQs | In His Hands Birth Supply' } },
	{ path: '/terms-and-conditions', component: TermsAndConditions, meta: { title: 'Terms & Conditions | In His Hands Birth Supply' } },
	{ path: '/discount-code-terms', component: DiscountTerms, meta: { title: 'Discount Code Terms | In His Hands Birth Supply' } },
	{ path: '/sitemap', component: SiteMap, meta: { title: 'Sitemap | In His Hands Birth Supply' } },
	{ path: '/sitemap-brands', component: SitemapBrands, meta: { title: 'Sitemap Brands | In His Hands Birth Supply' } },
	{ path: '/sitemap-categories', component: SitemapCategories, meta: { title: 'Sitemap Categories | In His Hands Birth Supply' } },
	{ path: '/privacy-policy', component: PrivacyPolicy, meta: { title: 'Privacy Policy | In His Hands Birth Supply' } },
	{ path: '/sms', component: SMS, meta: { title: 'SMS | In His Hands Birth Supply' } },
	{ path: '/birth-pool-setup', component: BirthPoolSetup, meta: { title: 'Birth Pool Setup | In His Hands Birth Supply' } },
	{ path: '/newman-doppler-trade-in', component: NewmanDopplerTradeInOffer, meta: { title: 'Newman Doppler Trade In | In His Hands Birth Supply' } },
	{ path: '/gift-certificates', component: GiftCertificates, meta: { title: 'Gift Certificates | In His Hands Birth Supply' } },
	{ path: '/search', component: Search, meta: { title: 'Search | In His Hands Birth Supply' } },
	{ path: '/:pathMatch(.*)*', component: NotFound, meta: { title: '404 Not Found' } },
]

const router = createRouter({
	history: createWebHistory(),
	scrollBehavior(to, from, savedPosition) {
		let toPath = to.matched?.[0]?.path

		let sameComponent = to.matched?.[0]?.components?.default == from.matched?.[0]?.components?.default
		let sameComponentExceptions = ['/inventory/:id', /account\/*/g]
		let exceptionExists = sameComponentExceptions.some((e) => !!toPath?.match(e)?.length)

		if (to.hash) {
			return { el: to.hash, behavior: 'smooth' }
		}
		if (savedPosition) return savedPosition
		if (sameComponent && !exceptionExists) return null

		return { top: 0 }
	},
	routes,
})

const app = createApp(App)

app.use(router)
app.use(Vue3TouchEvents)
app.use(MotionPlugin)
app.use(VueClipboard, {
	autoSetContainer: true,
	appendToBody: true,
})

import * as Sentry from '@sentry/vue'
Sentry.init({
	app,
	dsn: 'https://d78fb61eab425e4210c7b86567e570ef@o4506316278595584.ingest.us.sentry.io/4509118260838400',
})

// myUndefinedFunction()

/*

Global Properties

*/

import breakpoints from './helpers/breakpoints'
import darkMode from './helpers/darkMode'
import formatPhone from './helpers/formatPhone'
import { initUser } from './helpers/user'
import initCart from './helpers/cart'
import saveCart from './helpers/cart'

app.provide('breakpoints', breakpoints().breakpoints)
app.provide('darkMode', darkMode().darkMode)

let api = useDevAPI ? `http://localhost:${port}` : apiDomain
if (!isDev) api = apiDomain

let editingKitId = window.localStorage.getItem('editingKitId')

const store = useStore()

store.initUserCart = async () => {
	console.log('Initializing user cart...')
	store.user = await initUser()
	let cart = await initCart(store.user, api)
	store.cart = cart.cart
	store.itemsCount = cart.itemsCount
	store.certificatesCount = cart.certificatesCount
	store.cartTotalItems = cart.cartTotalItems
	store.cartSubtotal = cart.cartSubtotal
	store.texasStateSalesTax = cart.texasStateSalesTax
	store.originalCart = cart.originalCart
	store.cartTotal = cart.cartTotal

	store.saveCart = async () => {
		await cart.saveCart()
	}
}

store.initUserCart().then(() => {
	store.cartInitialized = true
})

// store.saveCart = async () => {
// 	await saveCart()
// }

let Authorization = window.localStorage.getItem('token')
axios.defaults.headers.common['Authorization'] = Authorization

const checkout = useCheckout()

app.provide('store', store)
app.provide('checkout', checkout)
app.provide('axios', axios)

app.provide('mode', import.meta.env.MODE)
app.provide('formatPhone', formatPhone)

/*

Global Components

*/

import Mango from './helpers/Mango.vue'
import Spinner from './components/layout/spinner.vue'
import Modal from './components/layout/modal.vue'
import Button from './components/partials/button.vue'
// HerbalNatural import removed as it was unused

app.component('Mango', Mango)
app.component('Spinner', Spinner)
app.component('Modal', Modal)
app.component('btn', Button)
// app.component('resource', defineAsyncComponent(Layouter.vue')))

/*

Route Guard

*/

router.beforeEach(async (to, from, next) => {
	// Block access to BK edit pages if user is not logged in
	if (to.path.match(/\/birthKits\/(.+)\/edit/)) {
		if (!store.user?.id) {
			return next(to.path.replace('/edit', ''))
		}
	}

	if (to.meta?.protected && !store.user?.id) {
		console.log('protected', store.user?.id)
		store.login = { next: to.path }
		if (to.meta?.fallback) {
			console.log('to', to)
			// Get params from path and add them to the fallback path (/event/:id)
			let fallback = to.meta.fallback
				?.split('/')
				?.map((p) => {
					if (p.includes(':')) {
						let param = p.replaceAll(':', '')
						return to.params[param] || ''
					} else {
						return p
					}
				})
				?.join('/')
			return next(fallback || to.meta.fallback)
		}
		return next('')
	}

	if (to.meta?.title) {
		document.title = to.meta.title
	}

	next()

	// Google Analytics manual page_view event
	if (typeof window.gtag === 'function') {
		window.gtag('event', 'page_view', {
			page_title: to.meta?.title || document.title,
			page_location: window.location.origin + to.fullPath,
			send_to: 'G-SLGVZKFN0K', // Target GA property
		})
	}
	next()
})

app.mount('#app')
